/* =========================================================================
   SAMSARA — HEADER.JS
   - Scroll-based .header--scrolled class (rAF throttled)
   - Cart trigger → dispatch `cart:open` event
   - Menu trigger → dispatch `menu:open` event
   - Cart count updater → ascultă `cart:updated` (payload: { item_count })
   Vanilla ES6+, zero deps. IIFE namespace.
   ========================================================================= */

(function () {
  'use strict';

  /* -----------------------------------------------------------------
     Header transparent → solid pe scroll. Pattern editorial:
     IntersectionObserver pe sentinel 1px sub header-group. Când sentinel
     iese din viewport (user a scrollat past hero edge) → data-scrolled=true
     pe header → .header__bg fade in opacity 0 → 1 cu cubic-bezier 0.4s.
     Animation e GPU compositor-only (opacity pe child layer), zero scroll
     listener, zero jitter, smooth on all hardware.
     ----------------------------------------------------------------- */
  function initStickyHeader(header) {
    if (!header) return;

    // Pe pagini fără dark hero (collection, product, blog) — header solid
    // de la load (data-scrolled="true"), zero IO needed.
    const hasDarkHero = document.querySelector('.home-hero');
    if (!hasDarkHero) {
      header.dataset.scrolled = 'true';
      return;
    }

    const sentinel = document.querySelector('[data-header-sentinel]');
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      // Fallback no-IO: solid de la start (safe default)
      header.dataset.scrolled = 'true';
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // entry.isIntersecting === false → sentinel scrollat afară → solid
          header.dataset.scrolled = String(!entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(sentinel);
  }

  /* -----------------------------------------------------------------
     Ascundere la derulare in jos (reintrodusa 2026-08-24, cerere Iustin).

     Prima implementare a fost scoasa pentru jitter, fiindca anima `top` —
     layout la fiecare cadru de scroll. Aici schimbam DOAR un data-attribute,
     iar CSS-ul face translatia pe compozitor. Trei garzi impotriva
     nervozitatii:
       - prag de 8px: micro-miscarile si bounce-ul de la capete nu declanseaza
       - zona moarta de 96px sus: langa varful paginii headerul ramane vizibil
       - citirea pozitiei e throttled pe rAF, ca la restul temei

     Cand un drawer/modal e deschis, body primeste `is-scroll-locked` si
     scroll-ul se opreste oricum, deci nu e nevoie de tratament special.
     ----------------------------------------------------------------- */
  function initHideOnScroll(header) {
    if (!header || header.dataset.hideOnScroll !== 'true') return;

    var DELTA = 8;
    var TOP_ZONE = 96;
    var lastY = window.scrollY || window.pageYOffset || 0;
    var ticking = false;

    function evaluate() {
      ticking = false;
      var y = window.scrollY || window.pageYOffset || 0;
      var diff = y - lastY;

      if (Math.abs(diff) < DELTA) return;

      if (y <= TOP_ZONE) {
        header.dataset.hidden = 'false';
      } else if (diff > 0) {
        header.dataset.hidden = 'true';
      } else {
        header.dataset.hidden = 'false';
      }

      lastY = y;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(evaluate);
      },
      { passive: true }
    );

    // Focus pe ceva din header (navigare cu tastatura) il aduce inapoi.
    header.addEventListener('focusin', function () {
      header.dataset.hidden = 'false';
    });
  }

  /* -----------------------------------------------------------------
     Cart trigger → dispatch `cart:open`
     Păstrează link-ul <a href="/cart"> ca fallback progressive enhancement.
     ----------------------------------------------------------------- */
  function initCartTrigger(trigger) {
    if (!trigger) return;

    trigger.addEventListener('click', function (e) {
      // Dacă cart drawer NU e încă disponibil (T7), lasă link-ul să navigheze normal
      if (!document.querySelector('[data-cart-drawer]')) {
        return;
      }
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:open'));
    });
  }

  /* -----------------------------------------------------------------
     Menu trigger → dispatch `menu:open`
     ----------------------------------------------------------------- */
  function initMenuTrigger(trigger) {
    if (!trigger) return;

    trigger.addEventListener('click', function () {
      // Dispatch cu detail.trigger ca mobile-menu.js să știe unde să
      // returneze focus-ul după close (cerință A11y).
      document.dispatchEvent(
        new CustomEvent('menu:open', {
          detail: { trigger: trigger },
        })
      );
      trigger.setAttribute('aria-expanded', 'true');
    });

    document.addEventListener('menu:closed', function () {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /* -----------------------------------------------------------------
     Search trigger → placeholder T4; real search apare mai târziu
     ----------------------------------------------------------------- */
  function initSearchTrigger(trigger) {
    if (!trigger) return;

    trigger.addEventListener('click', function () {
      document.dispatchEvent(new CustomEvent('search:open'));
    });
  }

  /* -----------------------------------------------------------------
     Cart count updater — ascultă `cart:updated`, update DOM
     Infrastructura pusă acum; T7 va dispatch-ui event real
     cu payload { detail: { item_count: N } }.
     ----------------------------------------------------------------- */
  function initCartCountUpdater() {
    const countEls = document.querySelectorAll('[data-cart-count]');
    if (countEls.length === 0) return;

    document.addEventListener('cart:updated', function (event) {
      const count = event.detail && typeof event.detail.item_count === 'number'
        ? event.detail.item_count
        : null;
      if (count === null) return;

      countEls.forEach(function (el) {
        el.textContent = '(' + count + ')';
        el.setAttribute('data-count', String(count));
        // Puls scurt pe numărul din coș — confirmarea vizuală „a intrat în coș”
        // (drawer-ul nu se mai deschide automat la add). Restart animație prin reflow.
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });

      // Actualizează și aria-label pe cart trigger folosind template-ul tradus din data-attr
      const triggers = document.querySelectorAll('[data-cart-trigger]');
      triggers.forEach(function (t) {
        const template = t.getAttribute('data-cart-open-label');
        if (template) {
          t.setAttribute('aria-label', template.replace('__COUNT__', String(count)));
        }
      });
    });
  }

  /* -----------------------------------------------------------------
     Bootstrap la DOM ready
     ----------------------------------------------------------------- */
  function boot() {
    const header = document.querySelector('[data-header]');
    initStickyHeader(header);
    initHideOnScroll(header);
    initCartTrigger(document.querySelector('[data-cart-trigger]'));
    initMenuTrigger(document.querySelector('[data-menu-trigger]'));
    initSearchTrigger(document.querySelector('[data-search-trigger]'));
    initCartCountUpdater();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
